import { IS_DEV } from 'consts/environmental';

const cookieDomain = IS_DEV ? '.curology.dev' : '.curology.com';

const date = new Date();
date.setTime(date.getTime() + 1 * 24 * 60 * 60 * 1000);

const utcDateOneDayFromNow = date.toUTCString();
const utcDateInThePast = 'Thu, 01 Jan 1970 00:00:01 GMT';

// The IIFE function at the top comes from Osano's official API docs: https://developers.osano.com/cmp/javascript-api/developer-documentation-consent-javascript-api
// This code snippet exists to set or delete the "trackingOptOut" cookie whenever Osano consent is set or changed.
// To do that, the code below has to be loaded as the VERY FIRST script on the entire site, even before osano.js. This is necessary to hook into the internals of Osano and listen for the "onConsentSaved" event.
// Whenever consent is accepted or denied, "onConsentSaved" fires and we set or delete the "trackingOptOut" cookie accordingly.
// The "trackingOptOut" cookie follows the patient through their user journey and prevents back-end tracking events from firing.
export const OSANO_PRE_LOAD_SCRIPT = `
  try {
    // Track Osano load performance
    const osanoLoadStart = performance.now();
    
    (function (w, o, d) {
      w[o] =
        w[o] ||
        function () {
          w[o][d].push(arguments);
        };
      w[o][d] = w[o][d] || [];
    })(window, 'Osano', 'data');

    function handleCookieConsent(consent) {
        const hasNotConsentedToMarketing = consent.MARKETING === 'DENY';
        const hasOptedOutOfAllNonEssential = consent.OPT_OUT === 'ACCEPT';

        if (hasNotConsentedToMarketing || hasOptedOutOfAllNonEssential) {
            document.cookie = 'trackingOptOut=true; expires=${utcDateOneDayFromNow}; domain=${cookieDomain}; path=/; SameSite=Lax; Secure';
        } else {
            document.cookie = 'trackingOptOut=true; expires=${utcDateInThePast}; domain=${cookieDomain}; path=/; SameSite=Lax; Secure';
        }
        
        // Backup consent state to localStorage for resilience
        try {
          localStorage.setItem('osano_consent_backup', JSON.stringify({
            consent: consent,
            timestamp: Date.now()
          }));
        } catch (e) {
          // Silent fail for storage quota issues
        }
    }

    window.Osano('onConsentSaved', function (consent) {
        try {
          handleCookieConsent(consent);
        } catch (e) {
          // Fail safe: if Osano (or our handler) errors, opt out by default.
          document.cookie = 'trackingOptOut=true; expires=${utcDateOneDayFromNow}; domain=${cookieDomain}; path=/; SameSite=Lax; Secure';
        }
        
        // Track consent changes in analytics
        if (window.dataLayer) {
          window.dataLayer.push({
            event: 'consent_update',
            consent_categories: consent,
            consent_timestamp: new Date().toISOString()
          });
        }
    });

    // Tracking default consent state on initial pageload to ensure tracking is blocked for opt-out-by-default jurisdictions
    window.Osano('onInitialized', function() {
        try {
          const consent =
            window.Osano &&
            window.Osano.cm &&
            typeof window.Osano.cm.getConsent === 'function'
              ? window.Osano.cm.getConsent()
              : null;
          if (consent) {
            handleCookieConsent(consent);
          } else {
            document.cookie = 'trackingOptOut=true; expires=${utcDateOneDayFromNow}; domain=${cookieDomain}; path=/; SameSite=Lax; Secure';
          }
        } catch (e) {
          // Fail safe: if Osano errors, opt out by default.
          document.cookie = 'trackingOptOut=true; expires=${utcDateOneDayFromNow}; domain=${cookieDomain}; path=/; SameSite=Lax; Secure';
        }
        
        // Monitor Osano load time
        const loadTime = performance.now() - osanoLoadStart;
        
        // Send performance data to monitoring
        if (window.Sentry) {
          window.Sentry.addBreadcrumb({
            message: 'Osano initialized',
            data: { loadTime: Math.round(loadTime) },
            level: 'info'
          });
        }
        
        // Warn if slow to load
        if (loadTime > 3000) {
          console.warn('Osano slow to load: ' + Math.round(loadTime) + 'ms');
        }
    });
    
    // Timeout detection - apply safe defaults if Osano fails
    setTimeout(function() {
      if (!window.Osano || !window.Osano.cm) {
        console.warn('Osano failed to load within 5s, applying default consent');

        // Apply most restrictive consent by default (opt-out)
        document.cookie = 'trackingOptOut=true; expires=${utcDateOneDayFromNow}; domain=${cookieDomain}; path=/; SameSite=Lax; Secure';
        
        // Log failure to monitoring
        if (window.Sentry) {
          window.Sentry.captureMessage('Osano failed to load', {
            level: 'warning',
            tags: { component: 'osano' }
          });
        }
      }
    }, 5000);
  } catch (error) {
    console.error('Osano initialization error:', error);

    // Fail safe: opt-out by default
    document.cookie = 'trackingOptOut=true; expires=${utcDateOneDayFromNow}; domain=${cookieDomain}; path=/; SameSite=Lax; Secure';
    
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        tags: { component: 'osano_init' }
      });
    }
  }
`;
